import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
export function ExperiencesPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const experiences = [{
    title: 'Wildlife Safari',
    description: 'Experience game drives in Nairobi National Park, the only national park within a capital city.',
    duration: 'Half day to full day',
    bestFor: 'Nature lovers, photographers',
    info: 'See lions, rhinos, giraffes, and over 400 bird species against the city skyline backdrop.'
  }, {
    title: 'Cultural Tours',
    description: "Visit Bomas of Kenya, museums, and cultural centers to learn about Kenya's diverse heritage.",
    duration: '3-4 hours',
    bestFor: 'History enthusiasts, families',
    info: 'Experience traditional dances, music, and explore authentic homesteads from 16+ ethnic groups.'
  }, {
    title: 'Food Tours',
    description: "Explore Nairobi's culinary scene from street food markets to fine dining restaurants.",
    duration: '3-5 hours',
    bestFor: 'Food lovers, adventurous eaters',
    info: 'Taste nyama choma, mutura, mandazi, and discover hidden culinary gems across the city.'
  }, {
    title: 'City Walking Tours',
    description: "Discover Nairobi's history, architecture, and vibrant street life on guided walking tours.",
    duration: '2-3 hours',
    bestFor: 'Urban explorers, photographers',
    info: 'Walk through CBD, visit historic landmarks, and experience the pulse of modern Nairobi.'
  }, {
    title: 'Nature Walks',
    description: 'Hike through Karura Forest, Ngong Hills, or the Nairobi Arboretum for fresh air and exercise.',
    duration: '2-4 hours',
    bestFor: 'Hikers, bird watchers',
    info: 'Discover waterfalls, caves, and over 200 bird species in urban forest sanctuaries.'
  }, {
    title: 'Shopping Experiences',
    description: 'Browse craft markets, modern malls, and boutique shops for unique souvenirs and local products.',
    duration: 'Flexible',
    bestFor: 'Shoppers, souvenir hunters',
    info: 'From Maasai beadwork to contemporary art, find authentic Kenyan crafts and designs.'
  }];
  return <div className="min-h-screen bg-white" data-id="element-904">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-905">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20 md:py-32" data-id="element-906">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl" data-id="element-907">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-908"></div>
            <h1 className="text-7xl md:text-9xl font-light text-gray-900 mb-6 leading-[0.9] tracking-tight" data-id="element-909">
              Nairobi
              <br data-id="element-910" />
              <span className="font-serif italic" data-id="element-911">Experiences</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed font-light max-w-2xl" data-id="element-912">
              Discover unique activities and adventures that showcase the best
              of Nairobi.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-16" data-id="element-913">
        {/* Introduction */}
        <section className="mb-20" data-id="element-914">
          <div className="max-w-4xl mx-auto text-center" data-id="element-915">
            <div className="h-px w-16 bg-amber-500 mb-8 mx-auto" data-id="element-916"></div>
            <p className="text-gray-700 text-xl leading-relaxed font-light" data-id="element-917">
              Nairobi offers a diverse range of experiences for every type of
              traveler. Whether you're seeking wildlife encounters, cultural
              immersion, culinary adventures, or urban exploration, the city has
              something special to offer. From morning game drives to evening
              food tours, each experience provides a unique perspective on this
              dynamic African capital.
            </p>
          </div>
        </section>

        {/* Experiences Grid */}
        <section className="mb-20" data-id="element-918">
          <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-919"></div>
          <h2 className="text-4xl font-light text-gray-900 mb-12 tracking-tight" data-id="element-920">
            Popular Experiences
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-921">
            {experiences.map((experience, index) => <motion.div key={experience.title} className="border border-gray-200 p-8 hover:border-amber-500 transition-all" initial={{
            y: 20,
            opacity: 0
          }} whileInView={{
            y: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} data-id="element-922">
                <h3 className="text-2xl font-light text-gray-900 mb-4" data-id="element-923">
                  {experience.title}
                </h3>
                <p className="text-gray-700 leading-relaxed mb-4 font-light" data-id="element-924">
                  {experience.description}
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mb-6 font-light" data-id="element-925">
                  {experience.info}
                </p>
                <div className="space-y-2 text-sm text-gray-600 font-light pt-4 border-t border-gray-200" data-id="element-926">
                  <div data-id="element-927">
                    <span className="text-gray-900" data-id="element-928">Duration:</span>{' '}
                    {experience.duration}
                  </div>
                  <div data-id="element-929">
                    <span className="text-gray-900" data-id="element-930">Best for:</span>{' '}
                    {experience.bestFor}
                  </div>
                </div>
              </motion.div>)}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-20 pt-12 border-t border-gray-200" data-id="element-931">
          <div className="text-center max-w-2xl mx-auto" data-id="element-932">
            <div className="h-px w-16 bg-amber-500 mb-8 mx-auto" data-id="element-933"></div>
            <h2 className="text-4xl font-light text-gray-900 mb-6 tracking-tight" data-id="element-934">
              Ready to explore?
            </h2>
            <p className="text-gray-700 mb-8 text-lg font-light leading-relaxed" data-id="element-935">
              Browse our curated list of places and start planning your Nairobi
              adventure.
            </p>
            <Link to="/places" onClick={() => window.scrollTo(0, 0)} className="inline-flex items-center gap-3 px-10 py-4 bg-gray-900 text-white hover:bg-gray-800 transition-colors text-sm uppercase tracking-widest font-light" data-id="element-936">
              <span data-id="element-937">Explore places</span>
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} data-id="element-938" />
            </Link>
          </div>
        </section>
      </div>

      <ScrollToTop data-id="element-939" />
      <Footer data-id="element-940" />
    </div>;
}